import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Star, CheckCircle2, XCircle, Trash2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const StarRow = ({ rating }) => (
  <div className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        className={`w-3.5 h-3.5 ${n <= rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`}
        strokeWidth={1.5}
      />
    ))}
  </div>
);

const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-700 border-amber-200',
  approved: 'bg-green-100 text-green-700 border-green-200',
  rejected: 'bg-red-100 text-red-700 border-red-200',
};

const CourseReviewsTab = () => {
  const sessionToken = localStorage.getItem('admin_session_token') || '';
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState('');

  const load = useCallback(async () => {
    if (!sessionToken) return;
    setLoading(true);
    try {
      const q = new URLSearchParams({ session_token: sessionToken });
      if (filter !== 'all') q.set('status', filter);
      const res = await fetch(`${API_URL}/api/courses/admin/reviews?${q.toString()}`);
      const j = await res.json();
      if (!res.ok) throw new Error(j.detail || 'Load failed');
      setReviews(j.reviews || []);
      setStats(j.stats || { total: 0, pending: 0, approved: 0, rejected: 0 });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [filter, sessionToken]);

  useEffect(() => {
    load();
  }, [load]);

  const callAction = async (id, action, method = 'POST') => {
    setActionId(`${id}_${action}`);
    try {
      const res = await fetch(`${API_URL}/api/courses/admin/reviews/${id}/${action}?session_token=${encodeURIComponent(sessionToken)}`, {
        method,
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.detail || 'Failed');
      toast.success(j.message || 'Done');
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setActionId('');
    }
  };

  const deleteReview = async (id) => {
    if (!window.confirm('Delete this review permanently?')) return;
    setActionId(`${id}_delete`);
    try {
      const res = await fetch(`${API_URL}/api/courses/admin/reviews/${id}?session_token=${encodeURIComponent(sessionToken)}`, {
        method: 'DELETE',
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.detail || 'Failed');
      toast.success('Deleted');
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setActionId('');
    }
  };

  return (
    <div className="space-y-6" data-testid="course-reviews-tab">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">⭐ Course Reviews</h2>
        <p className="text-sm text-gray-600 mt-1">
          Moderate student reviews. Only <span className="font-semibold text-green-700">approved</span> reviews appear on the public course page.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {['total', 'pending', 'approved', 'rejected'].map((k) => (
          <Card key={k} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilter(k === 'total' ? 'all' : k)}>
            <CardContent className="p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wider">{k}</p>
              <p className={`text-2xl font-bold ${k === 'pending' ? 'text-amber-600' : k === 'approved' ? 'text-green-600' : k === 'rejected' ? 'text-red-600' : 'text-gray-900'}`}>
                {stats[k] || 0}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        {['pending', 'approved', 'rejected', 'all'].map((f) => (
          <Button
            key={f}
            size="sm"
            variant={filter === f ? 'default' : 'outline'}
            onClick={() => setFilter(f)}
            data-testid={`filter-${f}`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </Button>
        ))}
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : reviews.length === 0 ? (
        <p className="text-sm text-gray-500" data-testid="no-reviews">No reviews in this filter.</p>
      ) : (
        <div className="space-y-3" data-testid="reviews-list">
          {reviews.map((r) => (
            <Card key={r.id} className="border-gray-200" data-testid={`review-card-${r.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900">{r.user_name}</span>
                      <StarRow rating={r.rating} />
                      <Badge className={`${STATUS_COLORS[r.status] || ''} text-[10px] uppercase tracking-wider border`}>
                        {r.status}
                      </Badge>
                      {r.is_featured && (
                        <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px]">Featured</Badge>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {r.course_title || r.course_id} · {r.user_email || 'no email'}
                    </p>
                  </div>
                  <span className="text-[11px] text-gray-400">
                    {new Date(r.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                  </span>
                </div>
                <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed mb-3">{r.content}</p>

                <div className="flex flex-wrap gap-2">
                  {r.status !== 'approved' && (
                    <Button
                      size="sm"
                      onClick={() => callAction(r.id, 'approve')}
                      disabled={actionId === `${r.id}_approve`}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      data-testid={`approve-${r.id}`}
                    >
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                      Approve
                    </Button>
                  )}
                  {r.status !== 'rejected' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => callAction(r.id, 'reject')}
                      disabled={actionId === `${r.id}_reject`}
                      className="border-red-300 text-red-700 hover:bg-red-50"
                      data-testid={`reject-${r.id}`}
                    >
                      <XCircle className="w-3.5 h-3.5 mr-1" />
                      Reject
                    </Button>
                  )}
                  {r.status === 'approved' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => callAction(r.id, 'feature')}
                      disabled={actionId === `${r.id}_feature`}
                      className="border-amber-300 text-amber-700 hover:bg-amber-50"
                      data-testid={`feature-${r.id}`}
                    >
                      <Sparkles className="w-3.5 h-3.5 mr-1" />
                      {r.is_featured ? 'Unfeature' : 'Feature'}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => deleteReview(r.id)}
                    disabled={actionId === `${r.id}_delete`}
                    className="border-gray-300 text-gray-600 hover:bg-gray-50"
                    data-testid={`delete-${r.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1" />
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default CourseReviewsTab;
